/**
 * User Navigation Component
 * Provides consistent navigation across user pages with role-based menu items
 *
 * @author Zubduq
 * @version 1.0.0
 */

// Import the new icon system

// Import ResizeManager for centralized resize handling
import resizeManager from '../../utils/resizeManager.js';

// Import component lifecycle manager
import componentLifecycleManager from '../../utils/componentLifecycleManager.js';

/**
 * User Navigation Manager Class
 */
class UserNavigationManager {
  constructor() {
    // Prevent multiple instances
    if (UserNavigationManager.instance) {
      console.log('✅ UserNavigationManager already exists, returning existing instance');
      return UserNavigationManager.instance;
    }

    console.log('🚀 UserNavigationManager constructor called');

    // Register component with lifecycle manager
    componentLifecycleManager.registerComponent('UserNavigation', this);

    // Initialize component state
    this.currentUser = null;
    this.currentPage = this.getCurrentPage();
    this.initialized = false;

    // Initialize flags for preventing duplicate operations
    this.navigationInitialized = false;

    // Initialize resource tracking
    this.eventListeners = new Map();
    this.timeouts = new Set();
    this.domElements = new Map();
    this.resizeHandler = null;
    this.mediaQueryHandler = null;
    this.flyoutHandlers = new Map();
    this.previousMobileState = null;

    console.log('Current page detected:', this.currentPage);

    // Store instance reference
    UserNavigationManager.instance = this;

    this.init();
  }

  /**
   * Add event listener with tracking
   */
  addEventListenerWithTracking(element, event, handler, options = {}) {
    if (!element) return;

    element.addEventListener(event, handler, options);

    if (!this.eventListeners.has(element)) {
      this.eventListeners.set(element, []);
    }
    this.eventListeners.get(element).push({ event, handler, options });
  }

  /**
   * Initialize navigation and breadcrumb system
   */
  async init() {
    if (this.initialized) {
      console.log('🚫 UserNavigation already initialized, skipping');
      return;
    }

    // Skip flyout menu on booking pages
    const currentPath = window.location.pathname;
    if (currentPath.includes('book-appointment.html') || currentPath.includes('booking-confirmation.html')) {
      console.log('📄 Skipping UserNavigation on booking pages');
      this.initialized = true;
      return;
    }

    // Skip navigation on auth pages (login, register, etc.)
    if (currentPath.includes('/auth/')) {
      console.log('🔐 Skipping UserNavigation on auth pages');
      this.initialized = true;
      return;
    }

    console.log('🔧 UserNavigation init() called');
    try {
      await this.getCurrentUser();
      console.log('Current user loaded:', this.currentUser);

      this.enhanceNavigation();
      this.addBreadcrumb();

      // Add other enhancements if user is available
      if (this.currentUser) {
        this.addContextHelp();
      }

      this.initialized = true;
      console.log('✅ UserNavigation initialization complete');

    } catch (error) {
      console.error('Navigation initialization error:', error);

      // Still try to add basic breadcrumb even if user fetch fails
      console.log('Adding fallback breadcrumb due to error');
      this.addBreadcrumb();
      this.initialized = true;
    }
  }

  /**
   * Get current user from authentication
   */
  async getCurrentUser() {
    try {
      // First try to get user from UserBar if it's already available
      if (window.getCurrentUser) {
        const user = window.getCurrentUser();
        if (user) {
          this.currentUser = user;
          return user;
        }
      }

      // Fallback to token-based user loading (similar to UserBar approach)
      const token = localStorage.getItem('jwtToken') || localStorage.getItem('publicToken') || localStorage.getItem('token');
      if (!token) return null;

      try {
        // Decode JWT token to get user info
        const payload = JSON.parse(atob(token.split(".")[1]));
        this.currentUser = {
          name: payload.name || payload.email,
          role: payload.role,
          email: payload.email,
          organizationId: payload.organizationId
        };
        return this.currentUser;
      } catch (tokenError) {
        console.warn('Could not decode token:', tokenError);
        return null;
      }
    } catch (error) {
      console.warn('Navigation: Could not get current user:', error);
      return null;
    }
  }

  /**
   * Get booking page URL based on organization settings
   * Returns queue booking page if enabled, otherwise regular booking
   * @param {string} relativePath - Path prefix (e.g., '../booking/', '/booking/')
   * @returns {string} Booking page URL
   */
  getBookingUrl(relativePath = '../booking/') {
    const org = window.userContextService?.getCurrentOrganization();
    const queueEnabled = org?.queueBookingEnabled || false;
    
    if (queueEnabled) {
      return `${relativePath}book-appointment.html?mode=queue`;
    }
    return `${relativePath}book-appointment.html`;
  }

  /**
   * Get current page identifier from URL path
   * Maps file names to logical page identifiers for breadcrumb system
   * @returns {string} Page identifier
   */
  getCurrentPage() {
    const path = window.location.pathname;
    let filename = path.split('/').pop().replace('.html', '');

    // Handle root and index pages
    if (!filename || filename === 'index') {
      // Check if we're at the root domain (main landing page)
      if (path === '/' || path === '/index.html' || path === '/index') {
        return 'home'; // Main landing page
      }

      // Determine default based on path structure for other areas
      if (path.includes('/admin/')) {
        return 'admin-dashboard';
      } else if (path.includes('/employee/')) {
        return 'employee-dashboard';
      } else if (path.includes('/customer/')) {
        return 'customer-dashboard';
      } else if (path.includes('/booking/')) {
        return 'book-appointment';
      }
      return 'home'; // Default to home for root
    }

    // Map alternative file names to standard identifiers
    const pageMap = {
      'index': 'home',
      'dashboard': 'employee-dashboard',
      'schedule': 'employee-appointments',
      'leads': 'employee-leads',
      'customers': 'employee-leads',
      'admin': 'admin-dashboard',
      'management': 'admin-dashboard'
    };

    return pageMap[filename] || filename;
  }

  /**
   * Get cached DOM element or query and cache it
   */
  getCachedElement(key, selector) {
    if (!this.domElements.has(key)) {
      const element = document.querySelector(selector);
      if (element) {
        this.domElements.set(key, element);
      }
      return element;
    }
    return this.domElements.get(key);
  }

  /**
   * Enhance existing navigation with role-based items
   */
  enhanceNavigation() {
    const nav = this.getCachedElement('topNav', '.top-controls nav');
    if (!nav || !this.currentUser) return;

    const userRole = this.currentUser.role?.toLowerCase();
    const navigationItems = this.getNavigationItems(userRole);

    // Create enhanced navigation
    const enhancedNav = this.createEnhancedNavigation(navigationItems);

    // Replace existing navigation items (keep logout)
    const logoutBtn = nav.querySelector('#logout-btn');
    nav.innerHTML = enhancedNav;

    // Re-add logout button
    if (logoutBtn) {
      nav.appendChild(logoutBtn);
    }
  }

  /**
   * Get navigation items based on user role
   */
  getNavigationItems(role) {
    const baseItems = [
      { id: 'employee-dashboard', label: 'Main Dashboard', icon: IconManager.get('home'), href: 'employee-dashboard.html' }
    ];

    if (role === 'admin') {
      return [
        ...baseItems,
        { id: 'admin-dashboard', label: 'Admin Dashboard', icon: IconManager.get('admin'), href: '../admin/admin-dashboard.html', primary: true },
        { id: 'appointments-management', label: 'All Appointments', icon: IconManager.get('appointments'), href: '../admin/appointments-management.html' },
        { id: 'queue-management', label: 'Queues', icon: IconManager.get('queue'), href: '../admin/queue-management.html' },
        { id: 'employee-dashboard', label: 'Employee View', icon: IconManager.get('dashboard'), href: 'employee-dashboard.html' },
        { id: 'employee-appointments', label: 'My Schedule', icon: IconManager.get('schedule'), href: 'employee-appointments.html' },
        { id: 'employee-leads', label: 'My Leads', icon: IconManager.get('leads'), href: 'employee-leads.html' },
        { id: 'book-appointment', label: 'Book Appointment', icon: IconManager.get('add'), href: this.getBookingUrl('../booking/') }
      ];
    } else if (role === 'employee') {
      return [
        ...baseItems,
        { id: 'employee-dashboard', label: 'Employee Dashboard', icon: IconManager.get('dashboard'), href: 'employee-dashboard.html', primary: true },
        { id: 'employee-appointments', label: 'My Schedule', icon: IconManager.get('appointments'), href: 'employee-appointments.html' },
        { id: 'book-appointment', label: 'Book Appointment', icon: IconManager.get('add'), href: this.getBookingUrl('../booking/') },
        { id: 'employee-leads', label: 'My Leads', icon: IconManager.get('leads'), href: 'employee-leads.html' }
      ];
    } else if (role === 'public' || role === 'customer') {
      // Public users (Google Sign-In) navigation
      return [
        { id: 'customer-dashboard', label: 'My Dashboard', icon: IconManager.get('home'), href: '/customer/customer-dashboard.html', primary: true },
        { id: 'bookings', label: 'My Bookings', icon: IconManager.get('appointments'), href: '/customer/bookings.html' },
        { id: 'book-appointment', label: 'Book Appointment', icon: IconManager.get('add'), href: this.getBookingUrl('/booking/') },
        { id: 'guest-bookings', label: 'Guest Bookings', icon: IconManager.get('calendar'), href: '/customer/guest-bookings.html' }
      ];
    } else {
      return [
        ...baseItems,
        { id: 'book-appointment', label: 'Book Appointment', icon: IconManager.get('appointments'), href: this.getBookingUrl('../booking/'), primary: true },
        { id: 'employee-appointments', label: 'My Appointments', icon: IconManager.get('appointments'), href: 'employee-appointments.html' },
        { id: 'employee-leads', label: 'My Leads', icon: IconManager.get('leads'), href: 'employee-leads.html' }
      ];
    }
  }

  /**
   * Create enhanced navigation HTML
   */
  createEnhancedNavigation(items) {
    return items.map(item => {
      const isCurrentPage = this.currentPage === item.id;
      const buttonClass = isCurrentPage ? 'btn btn-primary' :
        item.primary ? 'btn btn-primary' : 'btn btn-secondary';

      const style = isCurrentPage ? 'opacity: 0.7; pointer-events: none;' : '';

      return `
        <a href="${item.href}" class="${buttonClass}" style="${style}">
          <span class="btn-icon">${item.icon}</span>
          <span class="btn-text">${item.label}</span>
        </a>
      `;
    }).join('');
  }

  /**
   * Add breadcrumb navigation to page header
   * Replaces any existing breadcrumb with dynamically generated one
   */
  addBreadcrumb() {
    console.log('🍞 addBreadcrumb() called');

    // Special handling for home page - only show breadcrumbs if user is logged in
    if (this.currentPage === 'home') {
      const breadcrumbWrapper = this.getCachedElement('breadcrumbWrapper', '#breadcrumb-wrapper');

      // Check if user is authenticated - support both organization users and public users
      const isAuthenticated = this.currentUser && (
        this.currentUser.role ||
        this.currentUser.isPublicUser ||
        this.currentUser.type === 'public_user'
      );

      if (!isAuthenticated) {
        // User not logged in - hide breadcrumb wrapper
        if (breadcrumbWrapper) {
          breadcrumbWrapper.classList.add('hidden');
        }
        console.log('🏠 Home page - hiding breadcrumbs for non-authenticated user');
        return;
      } else {
        // User is logged in - show breadcrumb wrapper
        if (breadcrumbWrapper) {
          breadcrumbWrapper.classList.remove('hidden');
        }
        console.log('🏠 Home page - showing breadcrumbs for authenticated user');
      }
    }

    // First try to find existing breadcrumb container in page header structure
    let breadcrumbContainer = this.getCachedElement('breadcrumbContainer', '.breadcrumb, #breadcrumb-container');
    let headerContainer = this.getCachedElement('headerContainer', '.page-header__branding');

    console.log('Breadcrumb container found:', !!breadcrumbContainer);
    console.log('Header container found:', !!headerContainer);

    // If no header container found, try alternative selectors
    if (!headerContainer) {
      headerContainer = this.getCachedElement('altHeaderContainer', '.top-controls');
      console.log('Alternative header container found:', !!headerContainer);
    }

    if (!headerContainer) {
      console.warn('No suitable container found for breadcrumb injection');
      return;
    }

    const breadcrumbHTML = this.createBreadcrumb();
    console.log('Generated breadcrumb HTML:', breadcrumbHTML ? 'YES' : 'NO');

    if (breadcrumbHTML) {
      if (breadcrumbContainer) {
        // Replace existing breadcrumb
        console.log('Replacing existing breadcrumb');
        breadcrumbContainer.outerHTML = breadcrumbHTML;
      } else {
        // Insert breadcrumb at the beginning of header container
        console.log('Inserting new breadcrumb');
        headerContainer.insertAdjacentHTML('afterbegin', breadcrumbHTML);
      }

      // Initialize flyouts after breadcrumb is added to DOM
      console.log('Setting up flyouts after breadcrumb insertion');
      this.setupFlyoutsForBreadcrumb();

    } else {
      // Hide existing breadcrumb container if no breadcrumb should be shown
      if (breadcrumbContainer) {
        breadcrumbContainer.classList.add('hidden');
        // Or remove it entirely
        // breadcrumbContainer.remove();
      }
    }
  }

  /**
   * Get comprehensive breadcrumb configuration for all pages
   * @returns {Object} Breadcrumb configuration object with role-based paths
   */
  getBreadcrumbConfiguration() {
    return {
      // Admin Pages
      'admin-dashboard': {
        admin: [
          { label: 'Home', href: '/admin/admin-dashboard.html', icon: IconManager.get('home') }
        ]
      },
      'organization-management': {
        admin: [
          { label: 'Home', href: '/admin/admin-dashboard.html', icon: IconManager.get('home') },
          { label: 'Organizations', href: '/admin/organization-management.html' }
        ]
      },
      'user-management': {
        admin: [
          { label: 'Home', href: '/admin/admin-dashboard.html', icon: IconManager.get('home') },
          { label: 'Organizations', href: '/admin/organization-management.html' },
          { label: 'Users', href: '/admin/user-management.html' }
        ]
      },
      'service-management': {
        admin: [
          { label: 'Home', href: '/admin/admin-dashboard.html', icon: IconManager.get('home') },
          { label: 'Organizations', href: '/admin/organization-management.html' },
          { label: 'Appointments', href: '/admin/appointments-management.html' },
          { label: 'Services', href: '/admin/service-management.html' }
        ]
      },
      'employee-service-management': {
        admin: [
          { label: 'Home', href: '/admin/admin-dashboard.html', icon: IconManager.get('home') },
          { label: 'Organizations', href: '/admin/organization-management.html' },
          { label: 'Appointments', href: '/admin/appointments-management.html' },
          { label: 'Employee Services', href: '/admin/employee-service-management.html' }
        ]
      },
      'product-management': {
        admin: [
          { label: 'Home', href: '/admin/admin-dashboard.html', icon: IconManager.get('home') },
          { label: 'Organizations', href: '/admin/organization-management.html' },
          { label: 'Products', href: '/admin/product-management.html' }
        ]
      },
      'landing-page-builder': {
        admin: [
          { label: 'Home', href: '/admin/admin-dashboard.html', icon: IconManager.get('home') },
          { label: 'Organizations', href: '/admin/organization-management.html' },
          { label: 'Configuration', href: '#' },
          { label: 'Public Page', href: '/admin/landing-page-builder.html' }
        ]
      },
      'organization-settings': {
        admin: [
          { label: 'Home', href: '/admin/admin-dashboard.html', icon: IconManager.get('home') },
          { label: 'Organizations', href: '/admin/organization-management.html' },
          { label: 'Configuration', href: '#' },
          { label: 'Organization Settings', href: '/admin/organization-settings.html' }
        ]
      },
      'landing-page-builder': {
        admin: [
          { label: 'Home', href: '/admin/admin-dashboard.html', icon: IconManager.get('home') },
          { label: 'Organizations', href: '/admin/organization-management.html' },
          { label: 'Configuration', href: '#' },
          { label: 'Public Page', href: '/admin/landing-page-builder.html' }
        ]
      },
      'organization-settings': {
        admin: [
          { label: 'Home', href: '/admin/admin-dashboard.html', icon: IconManager.get('home') },
          { label: 'Organizations', href: '/admin/organization-management.html' },
          { label: 'Configuration', href: '#' },
          { label: 'Organization Settings', href: '/admin/organization-settings.html' }
        ]
      },
      'availability-management': {
        admin: [
          { label: 'Home', href: '/admin/admin-dashboard.html', icon: IconManager.get('home') },
          { label: 'Organizations', href: '/admin/organization-management.html' },
          { label: 'Appointments', href: '/admin/appointments-management.html' },
          { label: 'Availability', href: '/admin/availability-management.html' }
        ]
      },
      'appointments-management': {
        admin: [
          { label: 'Home', href: '/admin/admin-dashboard.html', icon: IconManager.get('home') },
          { label: 'Organizations', href: '/admin/organization-management.html' },
          { label: 'Appointments', href: '/admin/appointments-management.html' }
        ]
      },
      'leads-management': {
        admin: [
          { label: 'Home', href: '/admin/admin-dashboard.html', icon: IconManager.get('home') },
          { label: 'Organizations', href: '/admin/organization-management.html' },
          { label: 'Leads', href: '/admin/leads-management.html' }
        ]
      },

      // Employee Pages
      'employee-dashboard': {
        employee: [
          { label: 'Dashboard', href: '/employee/employee-dashboard.html', icon: IconManager.get('home') }
        ]
      },
      'employee-appointments': {
        employee: [
          { label: 'Dashboard', href: '/employee/employee-dashboard.html', icon: IconManager.get('home') },
          { label: 'My Schedule', href: '/employee/employee-appointments.html' }
        ]
      },
      'employee-leads': {
        employee: [
          { label: 'Dashboard', href: '/employee/employee-dashboard.html', icon: IconManager.get('home') },
          { label: 'My Leads', href: '/employee/employee-leads.html' }
        ]
      },
      'queue-management': {
          employee: [
            { label: 'Dashboard', href: '/employee/employee-dashboard.html', icon: IconManager.get('home') },
            { label: 'Queues', href: '/employee/queue-management.html' }
          ],
          admin: [
            { label: 'Home', href: '/admin/admin-dashboard.html', icon: IconManager.get('home') },
            { label: 'Organizations', href: '/admin/organization-management.html' },
            { label: 'Queues', href: '/admin/queue-management.html' }
          ]
      },

      // Customer Pages
      'customer-dashboard': {
        customer: [
          { label: 'Dashboard', href: 'customer-dashboard.html', icon: IconManager.get('home') }
        ],
        public: [
          { label: 'Dashboard', href: 'customer-dashboard.html', icon: IconManager.get('home') }
        ]
      },
      'bookings': {
        customer: [
          { label: 'Dashboard', href: 'customer-dashboard.html', icon: IconManager.get('home') },
          { label: 'My Bookings', href: 'bookings.html' }
        ],
        public: [
          { label: 'Dashboard', href: 'customer-dashboard.html', icon: IconManager.get('home') },
          { label: 'My Bookings', href: 'bookings.html' }
        ]
      },
      'guest-bookings': {
        customer: [
          { label: 'Dashboard', href: 'customer-dashboard.html', icon: IconManager.get('home') },
          { label: 'Guest Bookings', href: 'guest-bookings.html' }
        ]
      },

      // Booking Pages
      'book-appointment': {
        employee: [
          { label: 'Dashboard', href: 'employee-dashboard.html', icon: IconManager.get('home') },
          { label: 'Book Appointment', href: this.getBookingUrl('') }
        ],
        customer: [
          { label: 'Dashboard', href: 'customer-dashboard.html', icon: IconManager.get('home') },
          { label: 'Book Appointment', href: this.getBookingUrl('') }
        ]
      },
      'booking-confirmation': {
        employee: [
          { label: 'Dashboard', href: 'employee-dashboard.html' },
          { label: 'Book Appointment', href: this.getBookingUrl('') },
          { label: 'Confirmation', href: 'booking-confirmation.html' }
        ],
        customer: [
          { label: 'Dashboard', href: 'customer-dashboard.html' },
          { label: 'Book Appointment', href: this.getBookingUrl('') },
          { label: 'Confirmation', href: 'booking-confirmation.html' }
        ]
      },

      // Home page (main landing page) - different breadcrumbs based on user role
      'home': {
        admin: [
          { label: 'Home', href: '/admin/admin-dashboard.html', icon: IconManager.get('home') }
        ],
        employee: [
          { label: 'Home', href: '/employee/employee-dashboard.html', icon: IconManager.get('home') }
        ],
        customer: [
          { label: 'Home', href: '/customer/customer-dashboard.html', icon: IconManager.get('home') }
        ],
        public: [
          { label: 'Home', href: '/customer/customer-dashboard.html', icon: IconManager.get('home') }
        ]
      }
    };
  }

  /**
   * Get flyout configuration for navigation menu
   * @returns {Object} Flyout configuration by user role
   */
  getFlyoutConfiguration() {
    return {
      home: {
        admin: {
          title: 'Home',
          description: 'Admin dashboard and management tools',
          sections: [
            {
              title: 'Organization',
              items: [
                { label: 'Organizations', href: '/admin/organization-management.html', icon: IconManager.get('organizations') }
              ]
            },
            {
              title: 'Users',
              items: [
                { label: 'Users', href: '/admin/user-management.html', icon: IconManager.get('team') }
              ]
            },
            {
              title: 'Leads',
              items: [
                { label: 'Leads', href: '/admin/leads-management.html', icon: IconManager.get('leads') }
              ]
            },
            {
              title: 'Appointments',
              items: [
                { label: 'Appointments', href: '/admin/appointments-management.html', icon: IconManager.get('appointments') },
                { label: 'Queues', href: '/admin/queue-management.html', icon: IconManager.get('queue') },
                { label: 'Services', href: '/admin/service-management.html', icon: IconManager.get('services') },
                { label: 'Products', href: '/admin/product-management.html', icon: IconManager.get('products') },
                { label: 'Employee Services', href: '/admin/employee-service-management.html', icon: IconManager.get('team') },
                { label: 'Availability', href: '/admin/availability-management.html', icon: IconManager.get('time') }
              ]
            },
            {
              title: 'Configuration',
              items: [
                { label: 'Organization Settings', href: '/admin/organization-settings.html', icon: IconManager.get('settings') },
                { label: 'Public Page', href: '/admin/landing-page-builder.html', icon: IconManager.get('page') }
              ]
            }
          ]
        },
        employee: {
          title: 'Home',
          description: 'Employee dashboard and tools',
          sections: [
            {
              title: 'Dashboard',
              items: [
                { label: 'My Dashboard', href: '/employee/employee-dashboard.html', icon: IconManager.get('dashboard') }
              ]
            },
            {
              title: 'Leads',
              items: [
                { label: 'My Leads', href: '/employee/employee-leads.html', icon: IconManager.get('leads') }
              ]
            },
            {
              title: 'Appointments',
              items: [
                { label: 'My Schedule', href: '/employee/employee-appointments.html', icon: IconManager.get('appointments') },
                { label: 'Queues', href: '/employee/queue-management.html', icon: IconManager.get('queue') },
                { label: 'Book Appointment', href: this.getBookingUrl('../booking/'), icon: IconManager.get('add') }
              ]
            }
          ]
        },
        customer: {
          title: 'Home',
          description: 'Customer dashboard and services',
          sections: [
            {
              title: 'Dashboard',
              items: [
                { label: 'My Dashboard', href: '/customer/customer-dashboard.html', icon: IconManager.get('dashboard') }
              ]
            },
            {
              title: 'Bookings',
              items: [
                { label: 'My Bookings', href: '/customer/bookings.html', icon: IconManager.get('appointments') },
                { label: 'Book Appointment', href: this.getBookingUrl('../booking/'), icon: IconManager.get('add') }
              ]
            }
          ]
        },
        // Public users (Google Sign-In) - same as customer
        public: {
          title: 'Home',
          description: 'Customer dashboard and services',
          sections: [
            {
              title: 'Dashboard',
              items: [
                { label: 'My Dashboard', href: '/customer/customer-dashboard.html', icon: IconManager.get('dashboard') }
              ]
            },
            {
              title: 'Bookings',
              items: [
                { label: 'My Bookings', href: '/customer/bookings.html', icon: IconManager.get('appointments') }
              ]
            }
          ]
        }
      },
      organization: {
        admin: {
          title: 'Organization',
          description: 'Organization management tools and settings',
          sections: [
            {
              title: 'Users',
              items: [
                { label: 'Users', href: '/admin/user-management.html', icon: IconManager.get('team') }
              ]
            },
            {
              title: 'Leads',
              items: [
                { label: 'Leads', href: '/admin/leads-management.html', icon: IconManager.get('leads') }
              ]
            },
            {
              title: 'Appointments',
              items: [
                { label: 'Appointments', href: '/admin/appointments-management.html', icon: IconManager.get('appointments') },
                { label: 'Queues', href: '/admin/queue-management.html', icon: IconManager.get('queue') },
                { label: 'Services', href: '/admin/service-management.html', icon: IconManager.get('services') },
                { label: 'Products', href: '/admin/product-management.html', icon: IconManager.get('products') },
                { label: 'Employee Services', href: '/admin/employee-service-management.html', icon: IconManager.get('team') },
                { label: 'Availability', href: '/admin/availability-management.html', icon: IconManager.get('time') }
              ]
            },
            {
              title: 'Configuration',
              items: [
                { label: 'Organization Settings', href: '/admin/organization-settings.html', icon: IconManager.get('settings') },
                { label: 'Public Page', href: '/admin/landing-page-builder.html', icon: IconManager.get('page') }
              ]
            }
          ]
        },
        employee: {
          title: 'Organization',
          description: 'Organization information and tools',
          sections: [
            {
              title: 'Users',
              items: [
                { label: 'Team Members', href: '#', icon: IconManager.get('team'), action: 'viewTeam' }
              ]
            },
            {
              title: 'Leads',
              items: [
                { label: 'My Leads', href: 'employee-leads.html', icon: IconManager.get('leads') }
              ]
            },
            {
              title: 'Appointments',
              items: [
                { label: 'My Schedule', href: 'employee-appointments.html', icon: IconManager.get('appointments') },
                { label: 'Book Appointment', href: this.getBookingUrl('../booking/'), icon: IconManager.get('add') }
              ]
            }
          ]
        },
        customer: {
          title: 'Organization',
          description: 'Organization services and information',
          sections: [
            {
              title: 'Users',
              items: [
                { label: 'Support', href: '#', icon: IconManager.get('support'), action: 'contactSupport' }
              ]
            },
            {
              title: 'Leads',
              items: [
                { label: 'Contact Us', href: '#', icon: IconManager.get('contact'), action: 'contactForm' }
              ]
            },
            {
              title: 'Bookings',
              items: [
                { label: 'Book Appointment', href: this.getBookingUrl('../booking/'), icon: IconManager.get('appointments') },
                { label: 'My Bookings', href: 'bookings.html', icon: IconManager.get('appointments') }
              ]
            }
          ]
        }
      },
      'appointments-management': {
        admin: {
          title: 'Appointments',
          sections: [
            {
              title: 'Appointments',
              items: [
                { label: 'Appointments', href: '/admin/appointments-management.html', icon: IconManager.get('appointments') },
                { label: 'Queues', href: '/admin/queue-management.html', icon: IconManager.get('queue') },
                { label: 'Services', href: '/admin/service-management.html', icon: IconManager.get('services') },
                { label: 'Products', href: '/admin/product-management.html', icon: IconManager.get('products') },
                { label: 'Employee Services', href: '/admin/employee-service-management.html', icon: IconManager.get('team') },
                { label: 'Availability', href: '/admin/availability-management.html', icon: IconManager.get('time') }
              ]
            }
          ]
        }
      },
      'leads-management': {
        admin: {
          title: 'Leads',
          sections: [
            {
              title: 'Leads',
              items: [
                { label: 'All Leads', href: '/admin/leads-management.html', icon: IconManager.get('leads') },
                { label: 'Lead Analytics', href: '#', icon: IconManager.get('analytics'), action: 'viewAnalytics' }
              ]
            }
          ]
        }
      },
      'landing-pages-settings': {
        admin: {
          title: 'Configuration',
          description: 'Manage your organization landing pages and configuration settings',
          sections: [
            {
              title: 'Settings',
              items: [
                { label: 'Organization Settings', href: '/admin/organization-settings.html', icon: IconManager.get('settings') },
                { label: 'Public Page', href: '/admin/landing-page-builder.html', icon: IconManager.get('page') }
              ]
            }
          ]
        }
      }
    };
  }

  /**
   * Get breadcrumb path for current page and user role
   * @param {string} pageId - Page identifier
   * @param {string} userRole - User role (admin, employee, customer)
   * @returns {Array|null} Array of breadcrumb items or null
   */
  getBreadcrumbPath(pageId = null, userRole = null) {
    const targetPage = pageId || this.currentPage;
    const targetRole = userRole || (this.currentUser && this.currentUser.role ? this.currentUser.role.toLowerCase() : 'employee');

    const breadcrumbConfig = this.getBreadcrumbConfiguration();
    const pageConfig = breadcrumbConfig[targetPage];

    if (!pageConfig || !pageConfig[targetRole]) {
      return null;
    }

    return pageConfig[targetRole];
  }

  /**
   * Get sibling pages for current breadcrumb level (future flyout enhancement)
   * @param {string} pageId - Page identifier
   * @param {string} userRole - User role
   * @returns {Array} Array of sibling page objects
   */
  getSiblingPages(pageId = null, userRole = null) {
    const targetPage = pageId || this.currentPage;
    const targetRole = userRole || (this.currentUser && this.currentUser.role ? this.currentUser.role.toLowerCase() : 'employee');

    const breadcrumbConfig = this.getBreadcrumbConfiguration();
    const siblings = [];

    // Find pages with similar breadcrumb depth and parent structure
    Object.entries(breadcrumbConfig).forEach(([page, roleConfig]) => {
      if (page === targetPage || !roleConfig[targetRole]) return;

      const currentPath = this.getBreadcrumbPath(targetPage, targetRole);
      const siblingPath = roleConfig[targetRole];

      // Pages are siblings if they have the same breadcrumb depth and parent
      if (currentPath && siblingPath &&
        currentPath.length === siblingPath.length &&
        currentPath.length > 1) {

        // Compare parent path (all items except the last)
        const currentParent = currentPath.slice(0, -1);
        const siblingParent = siblingPath.slice(0, -1);

        const sameParent = currentParent.length === siblingParent.length &&
          currentParent.every((item, index) =>
            item.label === siblingParent[index].label
          );

        if (sameParent) {
          siblings.push({
            id: page,
            label: siblingPath[siblingPath.length - 1].label,
            href: siblingPath[siblingPath.length - 1].href,
            current: page === targetPage
          });
        }
      }
    });

    return siblings;
  }
  /**
   * Create breadcrumb HTML with role-based navigation
   * Uses the comprehensive breadcrumb configuration and current user role
   * @returns {string|null} Breadcrumb HTML or null if not applicable
   */
  createBreadcrumb() {
    // Check if we have user data - support both organization users and public users
    if (!this.currentUser) {
      return this.createFallbackBreadcrumb();
    }

    // Determine user role - support public users
    let userRole = null;
    if (this.currentUser.role) {
      userRole = this.currentUser.role.toLowerCase();
    } else if (this.currentUser.isPublicUser || this.currentUser.type === 'public_user') {
      userRole = 'public';
    }

    // If we still don't have a role, use fallback
    if (!userRole) {
      return this.createFallbackBreadcrumb();
    }

    const breadcrumbConfig = this.getBreadcrumbConfiguration();
    const pageConfig = breadcrumbConfig[this.currentPage];

    if (!pageConfig || !pageConfig[userRole]) {
      console.log(`No breadcrumb config for page "${this.currentPage}" and role "${userRole}"`);
      return this.createFallbackBreadcrumb();
    }

    const breadcrumbs = pageConfig[userRole];

    // Show breadcrumb even for single items (current page) for consistency
    if (!breadcrumbs || breadcrumbs.length === 0) {
      return this.createFallbackBreadcrumb();
    }

    return `
      <nav class="breadcrumb" aria-label="Page navigation">
        ${breadcrumbs.map((crumb, index) => {
      const isLast = index === breadcrumbs.length - 1;
      const iconHtml = crumb.icon ? `<span class="breadcrumb__home">${crumb.icon}</span>` : '';

      // Check if this breadcrumb item should have a flyout
      const shouldHaveFlyout = this.shouldBreadcrumbHaveFlyout(crumb.label);

      // Special case: Home/Dashboard should ALWAYS have flyout, even if last
      const normalizedLabel = crumb.label.toLowerCase().trim();
      const isHomeOrDashboard = normalizedLabel === 'home' || normalizedLabel === 'dashboard';

      // If it's the last item AND not Home/Dashboard, render as current page (non-clickable)
      if (isLast && !isHomeOrDashboard) {
        return `
              <div class="breadcrumb__item">
                <span class="breadcrumb__link breadcrumb__link--current">
                  ${iconHtml}${crumb.label}
                </span>
              </div>
            `;
      } else {
        // Render as clickable link for non-last items OR Home/Dashboard
        const contextualUrl = this.getContextualUrl(crumb.href);
        const orgAwareHref = this.addOrganizationToUrl(contextualUrl);

        const flyoutAttr = shouldHaveFlyout ? ' data-has-flyout="true"' : '';

        // Determine flyout key based on the label
        const flyoutKey = this.getFlyoutKeyFromLabel(crumb.label);
        const flyoutDataAttr = flyoutKey ? ` data-flyout="${flyoutKey}"` : '';

        console.log(`Breadcrumb "${crumb.label}" should have flyout:`, shouldHaveFlyout, 'flyoutKey:', flyoutKey);

        return `
              <div class="breadcrumb__item"${flyoutAttr}>
                <a href="${orgAwareHref}" class="breadcrumb__link"${flyoutDataAttr}>
                  ${iconHtml}${crumb.label}
                </a>
              </div>
            `;
      }
    }).join('')}
      </nav>
    `;
  }

  /**
   * Check if a breadcrumb item should have a flyout based on its label
   * @param {string} label - Breadcrumb label
   * @returns {boolean} Whether the breadcrumb should have a flyout
   */
  shouldBreadcrumbHaveFlyout(label) {
    const normalizedLabel = label.toLowerCase().trim();

    // Home and Dashboard should ALWAYS have a flyout, regardless of page
    if (normalizedLabel === 'home' || normalizedLabel === 'dashboard') {
      return true;
    }

    // Define other breadcrumb labels that should have flyouts
    const flyoutLabels = [
      'organizations',
      'organization',
      'appointment management',
      'appointments management',
      'leads management',
      'configuration',
      'settings'
    ];

    return flyoutLabels.some(flyoutLabel =>
      normalizedLabel.includes(flyoutLabel)
    );
  }

  /**
   * Get flyout key from breadcrumb label
   * @param {string} label - Breadcrumb label
   * @returns {string|null} Flyout key or null
   */
  getFlyoutKeyFromLabel(label) {
    const normalizedLabel = label.toLowerCase().trim();

    if (normalizedLabel === 'home' || normalizedLabel === 'dashboard') {
      return 'home';
    }

    if (normalizedLabel.includes('organization') && !normalizedLabel.includes('settings')) {
      return 'organization';
    }

    if (normalizedLabel.includes('appointment') && normalizedLabel.includes('management')) {
      return 'appointments-management';
    }

    if (normalizedLabel.includes('leads') && normalizedLabel.includes('management')) {
      return 'leads-management';
    }

    if (normalizedLabel === 'configuration') {
      return 'landing-pages-settings';
    }

    return null;
  }

  /**
   * Create a fallback breadcrumb for unauthenticated users or error states
   */
  createFallbackBreadcrumb() {
    console.log('🏠 createFallbackBreadcrumb() called - returning fallback breadcrumb with flyout');
    return `
      <nav class="breadcrumb" aria-label="Page navigation">
        <div class="breadcrumb__item" data-has-flyout="true">
          <a href="/admin/admin-dashboard.html" class="breadcrumb__link breadcrumb__link--current" data-flyout="home">
            <span class="breadcrumb__home">🏠</span>Home
          </a>
        </div>
      </nav>
    `;
  }

  /**
   * Add quick action buttons based on current page
   */
  addQuickActions() {
    // Quick actions feature removed for cleaner interface - no longer needed
    return;
  }

  /**
   * Add context-sensitive help
   */
  addContextHelp() {
    // Page help feature disabled for now - not rendering help text
    return;

    const helpMap = {
      'admin-dashboard': 'Comprehensive admin controls for managing all aspects of the CRM system',
      'appointments-management': 'View, manage, and export all appointments across all organizations',
      'leads-management': 'View, manage, and export all customer leads across all organizations',
      'employee-dashboard': 'View your appointment statistics, today\'s schedule, and manage your availability',
      'employee-appointments': 'Manage your appointment schedule, update appointment statuses, and view customer details',
      'book-appointment': 'Schedule a new appointment by selecting service, date, time, and providing customer details',
      'employee-leads': 'View and manage your customer leads with integrated appointment history'
    };

    const helpText = helpMap[this.currentPage];
    if (!helpText) return;

    // Add help text to breadcrumb area or main content
    const breadcrumb = this.getCachedElement('breadcrumb', '.breadcrumb');
    const mainContent = this.getCachedElement('mainContent', 'main, .page-content');

    if (breadcrumb) {
      breadcrumb.insertAdjacentHTML('afterend', `
        <p class="page-help" style="margin: 0.5rem 0 1rem 0; color: #6b7280; font-size: 0.875rem; font-weight: normal;">
          ${helpText}
        </p>
      `);
    } else if (mainContent) {
      mainContent.insertAdjacentHTML('afterbegin', `
        <p class="page-help" style="margin: 0 0 1rem 0; color: #6b7280; font-size: 0.875rem; font-weight: normal;">
          ${helpText}
        </p>
      `);
    }
  }

  /**
   * Get current organization ID from various sources
   * @returns {string|null} Organization ID or null if not found
   */
  getCurrentOrganizationId() {
    // First, try to get from URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const orgFromUrl = urlParams.get('org');
    if (orgFromUrl) {
      return orgFromUrl;
    }

    // Second, try to get from UserBar organization selector
    try {
      const orgSelector = document.getElementById('organization-selector');
      if (orgSelector && orgSelector.value) {
        return orgSelector.value;
      }
    } catch (error) {
      console.debug('Could not get organization from selector:', error);
    }

    // Third, try to get from current user token
    if (this.currentUser && this.currentUser.organizationId) {
      return this.currentUser.organizationId;
    }

    // Fourth, try to get from window.currentOrganizationId if set by other components
    if (window.currentOrganizationId) {
      return window.currentOrganizationId;
    }

    return null;
  }

  /**
   * Add organization parameter to URL if organization ID is available
   * @param {string} url - Base URL
   * @returns {string} URL with organization parameter
   */
  /**
   * Add organization parameter to URL if organization ID is available
   * Note: All user roles (admin, employee, customer) get organization context from UserContextService
   * which is managed globally via UserBar. URLs don't need org parameter.
   * @param {string} url - Base URL
   * @returns {string} URL without organization parameter (context managed by UserContextService)
   */
  addOrganizationToUrl(url) {
    // All users get organization context from UserContextService (window.userContextService)
    // which is managed by UserBar - no need to add org parameter to URLs
    return this.getContextualUrl(url);
  }

  /**
   * Get contextual URL based on current page location
   * @param {string} url - Original URL
   * @returns {string} Contextually correct URL
   */
  getContextualUrl(url) {
    // If URL starts with # or is already absolute, return as-is
    if (url.startsWith('#') || url.startsWith('http') || url.startsWith('/')) {
      return url;
    }

    const currentPath = window.location.pathname;
    const currentFolder = currentPath.split('/').slice(-2, -1)[0]; // Get folder before filename

    // If URL already has ../ prefix, it's already contextual
    if (url.startsWith('../')) {
      return url;
    }

    // Determine correct path based on current location
    if (currentPath.includes('/admin/')) {
      // We're in admin folder
      if (url.includes('employee-') || url.includes('customer-') || url.includes('book-appointment')) {
        // Navigate to different folder
        if (url.includes('employee-')) {
          return `../employee/${url}`;
        } else if (url.includes('customer-') || url.includes('appointments.html')) {
          return `../customer/${url}`;
        } else if (url.includes('book-appointment') || url.includes('booking-confirmation')) {
          return `../booking/${url}`;
        }
      }
      // Same folder (admin), return as-is
      return url;
    } else if (currentPath.includes('/employee/')) {
      // We're in employee folder
      if (url.includes('admin-') || url.includes('organization-') || url.includes('user-') ||
        url.includes('service-') || url.includes('appointments-management') || url.includes('leads-management')) {
        return `../admin/${url}`;
      } else if (url.includes('book-appointment') || url.includes('booking-confirmation')) {
        return `../booking/${url}`;
      } else if (url.includes('customer-') || url.includes('appointments.html')) {
        return `../customer/${url}`;
      }
      // Same folder (employee), return as-is
      return url;
    } else if (currentPath.includes('/customer/')) {
      // We're in customer folder
      if (url.includes('admin-') || url.includes('organization-') || url.includes('user-') ||
        url.includes('service-') || url.includes('appointments-management') || url.includes('leads-management')) {
        return `../admin/${url}`;
      } else if (url.includes('employee-')) {
        return `../employee/${url}`;
      } else if (url.includes('book-appointment') || url.includes('booking-confirmation')) {
        return `../booking/${url}`;
      }
      // Same folder (customer), return as-is
      return url;
    } else if (currentPath.includes('/booking/')) {
      // We're in booking folder
      if (url.includes('admin-') || url.includes('organization-') || url.includes('user-') ||
        url.includes('service-') || url.includes('appointments-management') || url.includes('leads-management')) {
        return `../admin/${url}`;
      } else if (url.includes('employee-')) {
        return `../employee/${url}`;
      } else if (url.includes('customer-') || url.includes('appointments.html')) {
        return `../customer/${url}`;
      }
      // Same folder (booking), return as-is
      return url;
    }

    // Default case - return as-is
    return url;
  }

  /**
   * Set up flyouts specifically for breadcrumb items (works even without authenticated user)
   */
  setupFlyoutsForBreadcrumb() {
    console.log('🎯 setupFlyoutsForBreadcrumb() called');
    console.log('Current user:', this.currentUser);

    const flyoutConfig = this.getFlyoutConfiguration();

    // Determine user role - support both organization users and public users
    let userRole = 'admin'; // Default fallback

    if (this.currentUser && this.currentUser.role) {
      userRole = this.currentUser.role.toLowerCase();
    } else if (this.currentUser && this.currentUser.isPublicUser) {
      // Handle public users (Google Sign-In) who might not have explicit role
      userRole = 'public';
    } else if (this.currentUser && this.currentUser.type === 'public_user') {
      // Another way to identify public users
      userRole = 'public';
    }

    console.log('🔑 Using role for flyout setup:', userRole, 'currentUser:', this.currentUser);

    // Find all breadcrumb items with data-flyout attribute
    const flyoutTriggers = document.querySelectorAll('.breadcrumb [data-flyout]');
    console.log('📍 Found breadcrumb flyout triggers:', flyoutTriggers.length);

    flyoutTriggers.forEach((trigger, index) => {
      const flyoutKey = trigger.getAttribute('data-flyout');
      const config = flyoutConfig[flyoutKey];

      console.log(`Breadcrumb trigger ${index}: flyoutKey="${flyoutKey}", config exists:`, !!config, 'userRole config exists:', config ? !!config[userRole] : false);

      if (config && config[userRole]) {
        console.log('✅ Attaching flyout to breadcrumb item:', flyoutKey, 'for role:', userRole);
        this.attachFlyoutEvents(trigger, config[userRole], flyoutKey);
      } else {
        console.log(`❌ No flyout config found for role "${userRole}" and key "${flyoutKey}"`);
        if (config) {
          console.log('Available roles for this flyout:', Object.keys(config));
        }
      }
    });

    // Also trigger mobile navigation setup (with viewport check)
    this.triggerMobileNavigationSetup();

    // Set up viewport change listener for responsive behavior
    this.setupViewportChangeListener();
  }

  /**
   * Set up listener for viewport changes to handle responsive behavior
   */
  setupViewportChangeListener() {
    // Use matchMedia for CSS-based responsive behavior
    // Match the exact same media query used in isMobileViewport()
    const mobileMediaQuery = window.matchMedia('(max-width: 767px)');

    // Track previous mobile state to detect actual transitions
    this.previousMobileState = this.isMobileViewport();

    // Create single coordinated viewport change handler
    const handleViewportChange = (source) => {
      const currentMobileState = this.isMobileViewport();
      const stateChanged = this.previousMobileState !== currentMobileState;

      console.log(`🔄 Coordinated viewport change (${source}), mobile: ${currentMobileState}${stateChanged ? ' [STATE CHANGED]' : ' [no change]'}`);

      if (stateChanged) {
        console.log(`📱 Viewport transition: ${this.previousMobileState ? 'mobile' : 'desktop'} → ${currentMobileState ? 'mobile' : 'desktop'}`);

        if (currentMobileState) {
          // Changed to mobile - trigger mobile setup
          console.log('📱 Transitioning to mobile viewport - triggering mobile setup');
          this.triggerMobileNavigationSetup();
        } else {
          // Changed to desktop - could clean up mobile-specific elements if needed
          console.log('🖥️ Transitioning to desktop viewport');
        }

        // Update previous state
        this.previousMobileState = currentMobileState;
      }
    };

    // Handle media query changes for CSS breakpoint transitions
    const handleMediaQueryChange = (mediaQuery) => {
      console.log(`📐 Media query change detected: mobile = ${mediaQuery.matches}`);
      handleViewportChange('mediaQuery');
    };

    // Store handler reference for cleanup
    this.mediaQueryHandler = handleMediaQueryChange;

    // Set up the media query listener with tracking
    if (mobileMediaQuery.addListener) {
      // Older browsers
      mobileMediaQuery.addListener(handleMediaQueryChange);
      // Track manually for older API
      this.eventListeners.set('mediaQuery-legacy', {
        element: mobileMediaQuery,
        type: 'change',
        handler: handleMediaQueryChange,
        cleanup: () => mobileMediaQuery.removeListener(handleMediaQueryChange)
      });
    } else {
      // Modern browsers - use tracked event listener
      this.addEventListenerWithTracking(
        mobileMediaQuery,
        'change',
        handleMediaQueryChange
      );
    }

    // Register with centralized ResizeManager instead of direct window listener
    const handleResizeEvent = (eventType) => {
      console.log(`� ResizeManager triggered viewport check (${eventType})`);
      handleViewportChange('resizeManager');

      // Update viewport CSS properties for better performance in positioning calculations
      this.updateViewportCSSProperties();
    };

    // Register resize handler with centralized manager
    const registered = resizeManager.register(handleResizeEvent);
    if (registered) {
      console.log('✅ UserNavigation registered with ResizeManager');
      console.log(`📊 Initial mobile state: ${this.previousMobileState}`);

      // Store reference for cleanup
      this.resizeHandler = handleResizeEvent;
    } else {
      console.error('❌ Failed to register UserNavigation with ResizeManager');
    }
  }

  /**
   * Initialize flyout functionality for navigation items
   */
  initializeFlyouts() {
    if (!this.currentUser) return;

    console.log('Initializing flyouts for user:', this.currentUser);

    const flyoutConfig = this.getFlyoutConfiguration();

    // Determine user role - support both organization users and public users
    let userRole = 'employee'; // Default fallback

    if (this.currentUser.role) {
      userRole = this.currentUser.role.toLowerCase();
    } else if (this.currentUser.isPublicUser || this.currentUser.type === 'public_user') {
      userRole = 'public';
    }

    console.log('Using role for flyout initialization:', userRole);

    // Find navigation links that should have flyouts
    const flyoutTriggers = document.querySelectorAll('[data-flyout]');
    console.log('Found flyout triggers:', flyoutTriggers.length);

    flyoutTriggers.forEach(trigger => {
      const flyoutKey = trigger.getAttribute('data-flyout');
      const config = flyoutConfig[flyoutKey];

      console.log('Processing flyout trigger:', flyoutKey, config ? 'found' : 'not found');

      if (config && config[userRole]) {
        console.log('Attaching flyout to:', flyoutKey, 'for role:', userRole);
        this.attachFlyoutEvents(trigger, config[userRole], flyoutKey);
      }
    });

    // For testing: automatically add flyout triggers to any existing "Organization" links
    this.addTestFlyoutTriggers();

    // Note: Mobile navigation setup is handled by setupFlyoutsForBreadcrumb()
    // No need to trigger it here since initializeFlyouts() is no longer called from init()
  }

  /**
   * Check if current viewport matches mobile breakpoint using CSS media queries
   * @returns {boolean} True if mobile viewport
   */
  isMobileViewport() {
    // Use CSS media query that matches the SCSS mobile-only mixin
    // SCSS: @media (max-width: #{map.get($breakpoints, md) - 1px}) = max-width: 767px
    return window.matchMedia('(max-width: 767px)').matches;
  }

  /**
   * Trigger mobile navigation setup for breadcrumb flyouts
   * Only triggers on mobile viewports or when needed
   * Prevents duplicate calls with setup-in-progress tracking
   */
  triggerMobileNavigationSetup() {
    // Check if mobile setup is already in progress
    if (this.mobileSetupInProgress) {
      console.log('⏳ Mobile navigation setup already in progress - skipping duplicate call');
      return;
    }

    // Check if we're on a mobile viewport before triggering setup
    const isMobile = this.isMobileViewport();

    if (!isMobile) {
      console.log('🖥️ Desktop viewport detected, skipping mobile navigation setup');
      return;
    }

    console.log('📱 Mobile viewport detected, triggering mobile navigation setup...');

    try {
      // Wait a bit for DOM to settle, then trigger mobile navigation setup
      setTimeout(() => {
        try {
          console.log('🔧 Executing mobile navigation setup...');

          // Dispatch a custom event that mobile navigation can listen for
          const event = new CustomEvent('breadcrumbsReady', {
            detail: {
              source: 'UserNavigation',
              timestamp: new Date().toISOString()
            }
          });
          document.dispatchEvent(event);

          console.log('✅ Mobile navigation setup event dispatched');
        } catch (setupError) {
          console.error('❌ Error during mobile navigation setup execution:', setupError);
        }
      }, 100, 'UserNavigation-mobile-setup');
    } catch (error) {
      console.error('❌ Error initiating mobile navigation setup:', error);
    }
  }

  /**
   * Attach flyout events to navigation trigger
   */
  attachFlyoutEvents(trigger, config, flyoutKey) {
    // Check if flyout events are already attached to prevent duplicates
    if (trigger._flyoutEventsAttached) {
      console.log('🚫 Flyout events already attached to:', flyoutKey, '- skipping duplicate');
      return;
    }

    let flyoutElement = null;
    let hideTimeout = null;
    let isVisible = false;

    const showFlyout = () => {
      if (hideTimeout) {
        clearTimeout(hideTimeout);
        hideTimeout = null;
      }

      if (isVisible && flyoutElement) return;

      // Hide other flyouts first
      this.hideOtherFlyouts(flyoutKey);

      if (!flyoutElement) {
        flyoutElement = this.createFlyoutElement(config, flyoutKey);
        document.body.appendChild(flyoutElement);
      }

      // Position flyout
      this.positionFlyout(flyoutElement, trigger);
      flyoutElement.style.display = 'block';
      isVisible = true;

      // Add hover events to flyout with tracking
      const flyoutMouseEnter = () => {
        if (hideTimeout) {
          clearTimeout(hideTimeout);
          hideTimeout = null;
        }
      };

      this.addEventListenerWithTracking(
        flyoutElement,
        'mouseenter',
        flyoutMouseEnter
      );

      this.addEventListenerWithTracking(
        flyoutElement,
        'mouseleave',
        hideFlyout
      );
    };

    const hideFlyout = () => {
      hideTimeout = setTimeout(() => {
        if (flyoutElement) {
          flyoutElement.style.display = 'none';
          isVisible = false;
        }
      }, 200);
    };

    // Add tracked event listeners to trigger
    this.addEventListenerWithTracking(
      trigger,
      'mouseenter',
      showFlyout
    );

    this.addEventListenerWithTracking(
      trigger,
      'mouseleave',
      hideFlyout
    );

    // Mark this trigger as having flyout events attached
    trigger._flyoutEventsAttached = true;

    console.log('✅ Flyout events attached to:', flyoutKey);

    // Store flyout reference for cleanup
    trigger._flyoutData = {
      element: flyoutElement,
      config: config,
      key: flyoutKey
    };
  }

  /**
   * Create flyout element from configuration
   */
  createFlyoutElement(config, flyoutKey) {
    const flyout = document.createElement('div');
    flyout.className = 'navigation-flyout';
    flyout.setAttribute('data-flyout-key', flyoutKey);

    let sectionsHTML = '';
    if (config.sections) {
      sectionsHTML = config.sections.map(section => {
        const itemsHTML = section.items.map(item => {
          const href = item.action ? '#' : this.addOrganizationToUrl(item.href);
          const clickHandler = item.action ? ` onclick="this.handleFlyoutAction('${item.action}')"` : '';

          return `
            <a href="${href}" class="flyout-item"${clickHandler}>
              <span class="flyout-item__icon">${item.icon || '📄'}</span>
              <span class="flyout-item__label">${item.label}</span>
            </a>
          `;
        }).join('');

        return `
          <div class="flyout-section">
            <h4 class="flyout-section__title">${section.title}</h4>
            <div class="flyout-section__items">
              ${itemsHTML}
            </div>
          </div>
        `;
      }).join('');
    }

    flyout.innerHTML = `
      <div class="flyout-content">
        ${sectionsHTML}
      </div>
    `;

    return flyout;
  }

  /**
   * Position flyout relative to trigger element
   * Uses CSS custom properties to minimize viewport calculations during mobile keyboard events
   */
  positionFlyout(flyout, trigger) {
    const rect = trigger.getBoundingClientRect();
    const flyoutRect = flyout.getBoundingClientRect();

    // Use CSS custom properties that are updated less frequently
    // to avoid heavy calculations during mobile keyboard show/hide
    const style = getComputedStyle(document.documentElement);
    const viewportWidth = parseInt(style.getPropertyValue('--viewport-width')) || window.innerWidth;
    const viewportHeight = parseInt(style.getPropertyValue('--viewport-height')) || window.innerHeight;

    // Update CSS custom properties if they don't exist
    if (!style.getPropertyValue('--viewport-width')) {
      document.documentElement.style.setProperty('--viewport-width', window.innerWidth + 'px');
      document.documentElement.style.setProperty('--viewport-height', window.innerHeight + 'px');
    }

    // Default position: below and to the right of trigger
    let left = rect.left;
    let top = rect.bottom + 5;

    // Adjust if flyout would go off-screen horizontally
    if (left + flyoutRect.width > viewportWidth) {
      left = rect.right - flyoutRect.width;
    }

    // Adjust if flyout would go off-screen vertically
    if (top + flyoutRect.height > viewportHeight) {
      top = rect.top - flyoutRect.height - 5;
    }

    // Ensure flyout stays within viewport
    left = Math.max(10, Math.min(left, viewportWidth - flyoutRect.width - 10));
    top = Math.max(10, Math.min(top, viewportHeight - flyoutRect.height - 10));

    flyout.style.left = `${left}px`;
    flyout.style.top = `${top}px`;
  }

  /**
   * Update viewport CSS custom properties (called less frequently than direct JS calculations)
   * This helps reduce performance impact during mobile keyboard show/hide events
   */
  updateViewportCSSProperties() {
    // Only update if viewport has actually changed significantly
    const currentWidth = window.innerWidth;
    const currentHeight = window.innerHeight;

    const lastWidth = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--viewport-width')) || 0;
    const lastHeight = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--viewport-height')) || 0;

    // Only update if change is significant (more than 50px to avoid minor scrollbar changes)
    if (Math.abs(currentWidth - lastWidth) > 50 || Math.abs(currentHeight - lastHeight) > 50) {
      document.documentElement.style.setProperty('--viewport-width', currentWidth + 'px');
      document.documentElement.style.setProperty('--viewport-height', currentHeight + 'px');
      console.log(`📐 Viewport CSS properties updated: ${currentWidth}x${currentHeight}`);
    }
  }

  /**
   * Hide other flyouts except the specified one
   */
  hideOtherFlyouts(exceptKey) {
    document.querySelectorAll('.navigation-flyout').forEach(flyout => {
      if (flyout.getAttribute('data-flyout-key') !== exceptKey) {
        flyout.style.display = 'none';
      }
    });
  }

  /**
   * Handle flyout action clicks
   */
  handleFlyoutAction(action) {
    console.log('Flyout action triggered:', action);
    // Implement action handlers as needed
    switch (action) {
      case 'viewTeam':
        console.log('View team action');
        break;
      case 'viewOrgInfo':
        console.log('View organization info action');
        break;
      case 'contactSupport':
        console.log('Contact support action');
        break;
      case 'contactForm':
        console.log('Contact form action');
        break;
      case 'viewAnalytics':
        console.log('View analytics action');
        break;
      default:
        console.log('Unknown action:', action);
    }
  }

  /**
   * Add test flyout triggers to existing navigation elements
   * This is a temporary method to test flyout functionality
   */
  addTestFlyoutTriggers() {
    console.log('Adding test flyout triggers...');
    console.log('Current page:', this.currentPage);
    console.log('Current path:', window.location.pathname);

    // Test URL resolution for debugging
    const testUrls = [
      'organization-management.html',
      'employee-appointments.html',
      'book-appointment.html',
      'appointments.html'
    ];

    console.log('URL Resolution Test:');
    testUrls.forEach(url => {
      const contextual = this.getContextualUrl(url);
      const withOrg = this.addOrganizationToUrl(url);
      console.log(`${url} -> contextual: ${contextual} -> withOrg: ${withOrg}`);
    });

    // Look for breadcrumb links that contain "Organization" or similar terms
    const breadcrumbLinks = document.querySelectorAll('.breadcrumb__link');
    console.log('Found breadcrumb links:', breadcrumbLinks.length);

    breadcrumbLinks.forEach(link => {
      const text = link.textContent.trim().toLowerCase();
      console.log('Breadcrumb link text:', text, 'href:', link.href);

      if (text.includes('home')) {
        if (!link.hasAttribute('data-flyout')) {
          link.setAttribute('data-flyout', 'home');
          console.log('Added home flyout to:', text);
        }
      } else if (text.includes('organization') || text.includes('admin') || text.includes('dashboard')) {
        if (!link.hasAttribute('data-flyout')) {
          link.setAttribute('data-flyout', 'organization');
          console.log('Added organization flyout to:', text);
        }
      }
    });

    // Look for navigation menu items
    const navLinks = document.querySelectorAll('nav a, .nav-link');
    console.log('Found nav links:', navLinks.length);

    navLinks.forEach(link => {
      const text = link.textContent.trim().toLowerCase();
      if (text.includes('organization')) {
        if (!link.hasAttribute('data-flyout')) {
          link.setAttribute('data-flyout', 'organization');
          console.log('Added organization flyout to nav link:', text);
        }
      }
      if (text.includes('appointment') && text.includes('management')) {
        if (!link.hasAttribute('data-flyout')) {
          link.setAttribute('data-flyout', 'appointments-management');
          console.log('Added appointments flyout to nav link:', text);
        }
      }
      if (text.includes('leads') && text.includes('management')) {
        if (!link.hasAttribute('data-flyout')) {
          link.setAttribute('data-flyout', 'leads-management');
          console.log('Added leads flyout to nav link:', text);
        }
      }
    });

    // Re-scan for flyout triggers after adding them
    this.scanForNewFlyoutTriggers();
  }

  /**
   * Get flyout content for mobile navigation
   * @param {string} flyoutType - Type of flyout (home, organization, appointments-management, etc.)
   * @returns {Object|null} Flyout configuration object or null
   */
  getFlyoutContent(flyoutType) {
    console.log('🔍 getFlyoutContent called for type:', flyoutType);

    const flyoutConfig = this.getFlyoutConfiguration();
    const userRole = this.currentUser && this.currentUser.role ? this.currentUser.role.toLowerCase() : 'employee';

    console.log('User role:', userRole);
    console.log('Available flyout config for type:', flyoutConfig[flyoutType]);

    if (!flyoutConfig[flyoutType] || !flyoutConfig[flyoutType][userRole]) {
      console.log('No flyout configuration found for type:', flyoutType, 'and role:', userRole);
      return null;
    }

    const content = flyoutConfig[flyoutType][userRole];
    console.log('Returning flyout content:', content);

    return content;
  }

  /**
   * Scan for newly added flyout triggers and attach events
   */
  scanForNewFlyoutTriggers() {
    const flyoutConfig = this.getFlyoutConfiguration();
    const userRole = this.currentUser.role ? this.currentUser.role.toLowerCase() : 'employee';

    document.querySelectorAll('[data-flyout]:not([data-flyout-initialized])').forEach(trigger => {
      const flyoutKey = trigger.getAttribute('data-flyout');
      const config = flyoutConfig[flyoutKey];

      if (config && config[userRole]) {
        console.log('Attaching events to new flyout trigger:', flyoutKey);
        this.attachFlyoutEvents(trigger, config[userRole], flyoutKey);
        trigger.setAttribute('data-flyout-initialized', 'true');
      }
    });
  }

  /**
   * Comprehensive cleanup method for proper memory management
   * Unregisters event listeners, clears references, and prevents memory leaks
   */
  cleanup() {
    console.log('🧹 UserNavigation comprehensive cleanup started');

    try {
      // Unregister from ResizeManager
      if (this.resizeHandler) {
        const unregistered = resizeManager.unregister(this.resizeHandler);
        if (unregistered) {
          console.log('✅ UserNavigation unregistered from ResizeManager');
        } else {
          console.warn('⚠️ Failed to unregister UserNavigation from ResizeManager');
        }
        this.resizeHandler = null;
      }

      // Clean up media query listener
      if (this.mediaQueryHandler) {
        // Check if we have a stored cleanup method for legacy browsers
        const legacyCleanup = this.eventListeners.get('mediaQuery-legacy');
        if (legacyCleanup && legacyCleanup.cleanup) {
          legacyCleanup.cleanup();
          console.log('✅ Legacy media query listener removed');
        }
        this.mediaQueryHandler = null;
      }

      // Clear all tracked event listeners
      this.eventListeners.forEach((events, element) => {
        // Check if events is an array (DOM event listeners) or object (special cleanup)
        if (Array.isArray(events)) {
          events.forEach(({ event, handler, options }) => {
            if (element && typeof element.removeEventListener === 'function') {
              element.removeEventListener(event, handler, options);
            }
          });
        } else if (events && typeof events === 'object' && events.cleanup) {
          // Handle special cleanup objects (like mediaQuery-legacy)
          events.cleanup();
          console.log('✅ Special cleanup executed for', element);
        }
      });
      this.eventListeners.clear();

      // Clear all tracked timeouts
      this.timeouts.forEach(timeoutId => {
        clearTimeout(timeoutId);
      });
      this.timeouts.clear();

      // Clear all flyout handlers and references
      this.flyoutHandlers.clear();

      // Remove all flyout elements from DOM
      document.querySelectorAll('.navigation-flyout').forEach(flyout => {
        if (flyout.parentNode) {
          flyout.parentNode.removeChild(flyout);
          console.log('🗑️ Removed flyout element from DOM');
        }
      });

      // Clear flyout data from triggers
      document.querySelectorAll('[data-flyout]').forEach(trigger => {
        if (trigger._flyoutData) {
          delete trigger._flyoutData;
        }
        if (trigger._flyoutEventsAttached) {
          delete trigger._flyoutEventsAttached;
        }
        trigger.removeAttribute('data-flyout-initialized');
      });

      // Clear DOM element cache
      this.domElements.clear();

      // Clear custom event listeners from DOM
      document.removeEventListener('breadcrumbsReady', this.handleBreadcrumbsReady);

      // Clear all component references and state
      this.currentUser = null;
      this.currentPage = null;
      this.navigationInitialized = false;
      this.mobileSetupInProgress = false;
      this.previousMobileState = null;
      this.initialized = false;

      // Clear resource tracking maps and sets
      this.eventListeners.clear();
      this.timeouts.clear();
      this.domElements.clear();

      // Clear CSS custom properties that we set
      const style = document.documentElement.style;
      style.removeProperty('--viewport-width');
      style.removeProperty('--viewport-height');

      // Note: Do not call unregisterComponent here as it creates circular dependency
      // The lifecycle manager will handle unregistration when calling this cleanup method

      // Clear singleton instance
      UserNavigationManager.instance = null;

      console.log('🧹 UserNavigation comprehensive cleanup completed successfully');

    } catch (error) {
      console.error('❌ Error during UserNavigation cleanup:', error);

      // Ensure singleton is cleared even if cleanup fails
      UserNavigationManager.instance = null;
    }
  }

  /**
   * Debug method to check current state
   * Useful for troubleshooting and verification
   */
  getDebugState() {
    return {
      initialized: this.initialized,
      navigationInitialized: this.navigationInitialized,
      mobileSetupInProgress: this.mobileSetupInProgress,
      previousMobileState: this.previousMobileState,
      currentMobileState: this.isMobileViewport(),
      hasCurrentUser: !!this.currentUser,
      hasResizeHandler: !!this.resizeHandler
    };
  }
}

// Static instance reference for singleton pattern
UserNavigationManager.instance = null;

// Export for use in other modules
export default UserNavigationManager;
